import { ColoursObj, VerticalExpander } from "@eventr/common-storybook";
import { Box, Stack } from "@mui/material";
import { useIsHeaderVisible } from "home/components/carousel/ItineraryCarousel";
import { RailHeaderControls } from "home/components/carousel/RailHeaderControls";
import i18n from "i18n/i18next";
import React, { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { routes } from "routing";
import { useHomeTabStore } from "state/itineraryState/homeTab.store";
import { getTranslationKeyFromHomeTab } from "state/itineraryState/homeTabs";
import { useShallow } from "zustand/react/shallow";
import { navbarTranslation } from "../../../i18n/translateHelpers";
import { useBoundStore } from "../../../state/bound.store";
import { useHasTeams } from "../../../state/hooks/useHasTeams";
import { useLanguageContext } from "../../../state/languageState/languageContext";
import { useRouteGuardContext } from "../../../state/routeGuardProvider";
import {
  useBottomInset,
  useKeyboardOpen,
  useLeftInset,
  useRightInset,
  useTopInset,
} from "../../../state/windowState/window.store";
import { useTabRailLeft } from "../tab-rail-side.store";
import { isWebPreview } from "../../../utils";
import { useGlobalRouter } from "../../globalRouter/useGlobalRouter";
import { useHomeScrollAway } from "../../home/home-scroll-away";
import { type HomeRailTab, type LayoutTabId, TAB_RAIL_WIDTH, useScreenLayout } from "../screen-layout";
import { ChatNavBarItem } from "./components/chatNavBarItem";
import { NavBarItem } from "./components/navBarItem";
import { TeamNavBarItem } from "./components/teamNavBarItem";
import { useNavBarStore, useTabBarDisabled } from "./navBar.store";
import { ITEM_PATHS } from "./tab-utils";
import { type DisabledTabProps, useDisabledTabProps } from "./tabDisabled.store";
import { resolveTabForPath, TAB_BY_ID, type TabId } from "./tabs";

export const navBarRef = React.createRef<HTMLDivElement>();

interface NavBarProps {
  /** Where the bar renders as the tab rail in the rail layout. */
  railContainer: HTMLElement | null;
}

export const NavBar = ({ railContainer }: NavBarProps) => {
  const { pathname } = useLocation();
  const { isAllowed } = useRouteGuardContext();
  const { pushPath } = useGlobalRouter();

  // A `useRef`, not `useState`: the counter is written and read back synchronously in the
  // same handler, and it is never read during render. `useState` would compare a stale
  // value against 10 and the easter egg would never fire.
  const taps = useRef(0);
  const tapsTimer = useRef<NodeJS.Timeout | null>(null);
  const currPage = window.location.href;
  const { selectedLanguage } = useLanguageContext();

  const { wpUser, noItineraries, accessRequests, selectedItinEmpty, profileRequiredAdditionalInfo } = useBoundStore(
    useShallow((s) => {
      return {
        wpUser: s.wpUser,
        noItineraries: s.noItineraries,
        accessRequests: s.accessRequests,
        selectedItinEmpty: s.selectedItinEmpty,
        profileRequiredAdditionalInfo: s.profileRequiredAdditionalInfo,
      };
    }),
  );

  const resolveActiveBasedOnPath = (path: string): TabId | undefined => {
    const matchedTab = resolveTabForPath(path);
    if (matchedTab) {
      return matchedTab.id;
    }
    // An item viewer keeps the highlight of the tab it was opened from.
    const isItem = ITEM_PATHS.some((regex) => regex.test(path));
    if (isItem) {
      return useNavBarStore.getState().activeNavBar;
    }
    return undefined;
  };

  // Derived during render, not in an effect, so the bar never shows for a frame on a
  // hidden route before an effect catches up. That lag, combined with the animated
  // VerticalExpander, would flash the bar in and then collapse it on the auth routes.
  const keyboardOpen = useKeyboardOpen();
  const topInset = useTopInset();
  const bottomInset = useBottomInset();
  const rightInset = useRightInset();
  const leftInset = useLeftInset();
  const tabRailLeft = useTabRailLeft();
  // The rail sits against one edge of the window and clears that edge's safe area.
  const railEdgeInset = tabRailLeft ? leftInset : rightInset;
  const { tabBar, tabs, highlightedTab, hidesTabBar, layout, leadingPane } = useScreenLayout();
  // Where Home's header shows, its menu, time mode and alerts are on the rail.
  const headerVisible = useIsHeaderVisible();
  const showsHeaderControls =
    headerVisible && (layout === "two-pane" ? leadingPane === "home" : pathname === routes.home.path);
  const rail = tabBar === "rail" && railContainer !== null;
  const pathHidden = isWebPreview || hidesTabBar === true;
  // Only the bottom bar collapses for the keyboard.
  const hidden = pathHidden || (keyboardOpen && !rail);
  const tabBarDisabled = useTabBarDisabled();

  // The bottom bar slides off the bottom as the home page scrolls; the rail stays. iOS native
  // navigation keeps its tab bar outside the web view and this component is not rendered there.
  const { setFooter } = useHomeScrollAway();
  useEffect(() => {
    if (rail) return;
    setFooter(navBarRef.current);
    return () => setFooter(null);
  }, [setFooter, rail]);

  // This bar's height is the bottom safe offset every FAB and page footer positions itself
  // against (useTabBarBottomSafeOffset). It is not a constant: the bar collapses to zero on
  // the TAB_BAR_HIDDEN_PATHS routes and grows back over VerticalExpander's 200ms
  // transition. Consumers used to read `navBarRef.current.offsetHeight` during their own
  // render, which subscribes to nothing — one that rendered mid-expansion measured 0, pinned
  // itself 16px off the bottom of the screen (underneath this bar, which outranks it at
  // z-index 1000) and stayed buried until some unrelated re-render happened to re-measure.
  // Returning from the new-itinerary form hit that every time: /edit/* hides the bar, so the
  // Home FAB's first render on arrival always landed inside the expansion. Publishing the
  // height instead makes it a subscription, and the observer fires for the whole animation.
  useEffect(() => {
    const element = navBarRef.current;
    const { setNavBarHeight } = useNavBarStore.getState();
    // The rail reports 0: it covers no part of the bottom edge.
    if (!element || rail) {
      setNavBarHeight(0);
      return;
    }

    const publish = () => setNavBarHeight(Math.round(element.offsetHeight));

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(element);
    return () => {
      observer.disconnect();
      // Nothing is drawing the bar any more, so nothing should reserve space for it. iOS
      // native navigation never mounts this component and keeps the value at its 0 default.
      setNavBarHeight(0);
    };
  }, [rail]);

  useEffect(() => {
    useNavBarStore.getState().setActiveNavBar(resolveActiveBasedOnPath(pathname));
  }, [pathname, selectedLanguage]);

  // Mirror the derived value into the shared store for the other nav-bar consumers.
  useEffect(() => {
    useNavBarStore.getState().setNavBarHidden(hidden);
  }, [hidden]);

  // TODO: remove eventually as we now use version number tap on account page
  const onAccountClick = () => {
    taps.current = taps.current + 1;
    if (tapsTimer.current == null) {
      tapsTimer.current = setTimeout(() => {
        taps.current = 0;
        tapsTimer.current = null;
      }, 5000);
    }
    if (taps.current === 10) {
      taps.current = 0;
      pushPath(routes.dev.menu.path);
    }
  };

  const showAccessRequests = useMemo(() => {
    return wpUser?.roles?.some((role) => {
      return ["administrator", "eventr_manager", "owner"].includes(role);
    });
  }, [wpUser]);

  // An account with no team has nothing behind either page.
  const hasTeams = useHasTeams();
  const homeDisabled = useDisabledTabProps("home");
  const accountDisabled = useDisabledTabProps("account");

  const hasAccessRequests = wpUser && showAccessRequests;
  const collectiveRequests = accessRequests.length + (profileRequiredAdditionalInfo?.length ?? 0);
  const totalRequests = hasAccessRequests ? collectiveRequests : (profileRequiredAdditionalInfo?.length ?? 0);

  const items: Record<LayoutTabId, React.ReactNode> = {
    home: (
      <NavBarItem
        key="home"
        id="navbarItemHome"
        label={navbarTranslation(TAB_BY_ID.home.label)}
        active={highlightedTab === "home"}
        path={TAB_BY_ID.home.route}
        icon={TAB_BY_ID.home.icon}
        {...homeDisabled}
        pulse={
          isAllowed(["allowedEdit"]) && noItineraries && (currPage.endsWith("team") || currPage.endsWith("rocketchat"))
        }
      />
    ),
    summary: <HomeTabRailItem key="summary" tab="summary" active={highlightedTab === "summary"} {...homeDisabled} />,
    map: <HomeTabRailItem key="map" tab="map" active={highlightedTab === "map"} {...homeDisabled} />,
    chat: hasTeams && <ChatNavBarItem key="chat" />,
    team: hasTeams && <TeamNavBarItem key="team" />,
    account: (
      <Box key="account" onClick={onAccountClick}>
        <NavBarItem
          id="navbarItemAccount"
          label={navbarTranslation(TAB_BY_ID.account.label)}
          active={highlightedTab === "account"}
          path={TAB_BY_ID.account.route}
          icon={TAB_BY_ID.account.icon}
          {...accountDisabled}
          pulse={!isAllowed(["allowedEdit"]) && selectedItinEmpty}
          count={totalRequests}
        />
      </Box>
    ),
  };
  const orderedItems = tabs.map((id) => items[id]);

  const barSx = {
    bgcolor: ColoursObj.backgrounds.navbar,
    pointerEvents: tabBarDisabled ? "none" : undefined,
    opacity: tabBarDisabled ? 0.5 : 1,
    transition: "opacity 0.2s",
  } as const;

  if (rail) {
    if (pathHidden) return null;
    return createPortal(
      <Stack
        className="navbar"
        ref={navBarRef}
        sx={{
          height: "100%",
          width: TAB_RAIL_WIDTH + railEdgeInset + "px",
          boxSizing: "border-box",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "12px",
          padding: tabRailLeft
            ? `${topInset + 12}px 0 ${bottomInset + 16}px ${railEdgeInset}px`
            : `${topInset + 12}px ${railEdgeInset}px ${bottomInset + 16}px 0`,
          boxShadow: `${tabRailLeft ? 4 : -4}px 0px 5px 0px rgba(16, 55, 92, 0.05)`,
          ...barSx,
        }}
      >
        {showsHeaderControls && <RailHeaderControls />}
        {orderedItems}
      </Stack>,
      railContainer,
    );
  }

  return (
    <Box
      className="navbar"
      ref={navBarRef}
      sx={{
        zIndex: 1000,
        boxShadow: "0px -4px 5px 0px rgba(16, 55, 92, 0.05)",
        position: "fixed",
        bottom: 0,
        width: "100%",
        ...barSx,
      }}
    >
      {/* Route-driven hides (e.g. entering chat) happen inside a View Transition, whose
          instant snapshot swap for the navbar (index.css ::view-transition-*(footer)) races
          this component's own 200ms collapse and can flash. Only animate for keyboard-driven
          hides, which have no view transition involved. */}
      <VerticalExpander open={!hidden} transition={pathHidden ? "0s" : "linear 0.2s"}>
        <Box
          sx={{
            padding: "0 0 " + bottomInset + "px 0",
          }}
        >
          <Stack
            direction="row"
            sx={{
              padding: "12px 23px 12px 23px",
              justifyContent: "space-between",
            }}
          >
            {orderedItems}
          </Stack>
        </Box>
      </VerticalExpander>
    </Box>
  );
};

interface HomeTabRailItemProps extends DisabledTabProps {
  tab: HomeRailTab;
  active: boolean;
}

/** A home tab on the rail: opens Home with that home tab chosen. Summary is labelled as Home. */
const HomeTabRailItem = ({ tab, ...props }: HomeTabRailItemProps) => (
  <NavBarItem
    id={`navbarItem${tab === "summary" ? "Home" : "Map"}`}
    label={tab === "summary" ? navbarTranslation(TAB_BY_ID.home.label) : i18n.t(getTranslationKeyFromHomeTab(tab))}
    path={TAB_BY_ID.home.route}
    icon={tab === "summary" ? TAB_BY_ID.home.icon : "Map"}
    onClick={() => useHomeTabStore.getState().setHomeTab(tab)}
    {...props}
  />
);
